import React from "react";
import {Link} from "@inertiajs/react";
import {bereichLabel} from "./format.js";
import {seitenName} from "./routeLabels.js";

/**
 * Die drei Verknüpfungs-Bausteine der Nutzungsauswertung (Spezifikation
 * Abschnitt 2, "Durchgängige Verknüpfung"): EIN Weg, wie ein Mitarbeiter-,
 * Bereichs- oder Seitenname überall in der Auswertung auf seine
 * Detailansicht verweist - Tabellen, Listen, Fließtext eingeschlossen.
 * Ohne diese gemeinsame Stelle würde jede neue Auswertungsseite den Link
 * anders bauen (mal href, mal onClick+router.get, mal gar keiner).
 *
 * Alle drei rendern reinen Fließtext (kein eigener Block), damit sie sich
 * in Tabellenzeilen, Aufzählungen und Karten einfügen, ohne das Layout zu
 * sprengen - Ausrichtung/Abstand bestimmt der Aufrufer über className.
 */

/** Mitarbeitername -> Einzelprofil (/nutzung/mitarbeiter/{id}). Ohne
 * bekannte ID (z. B. Systemereignis ohne Nutzer) bleibt es reiner Text.
 * Restliche Props (style, onClick, ...) werden durchgereicht, damit sich
 * der Link z. B. als farbige Marke einbetten lässt (siehe Wegverlauf im
 * Mitarbeiterprofil). */
export function MitarbeiterLink({id, name, className = "", ...rest}) {
    const text = name || (id ? `Nutzer ${id}` : "System");
    if (!id) {
        return <span className={`text-muted ${className}`} {...rest}>{text}</span>;
    }
    return (
        <Link href={`/nutzung/mitarbeiter/${id}`} className={`nutzung-link ${className}`} {...rest}>
            {text}
        </Link>
    );
}

/** Bereichsname -> Bereichs-Detailseite (/nutzung/bereiche/{bereich}). Der
 * Klarname kommt einheitlich über bereichLabel() aus format.js. */
export function BereichLink({bereich, className = "", ...rest}) {
    if (!bereich) {
        return <span className={`text-muted ${className}`} {...rest}>–</span>;
    }
    return (
        <Link href={`/nutzung/bereiche/${encodeURIComponent(bereich)}`} className={`nutzung-link ${className}`} {...rest}>
            {bereichLabel(bereich)}
        </Link>
    );
}

/** Technischer Routenname -> Klartext (über seitenName()) UND Verweis auf
 * die Seiten-Auswertung, gefiltert auf genau diese Route. Die Zielseite
 * /nutzung/seiten wertet den Parameter "route" aus, sobald sie gebaut ist -
 * bis dahin landet der Klick dort einfach auf der ungefilterten Übersicht,
 * kein Absturz. Technische Routennamen erscheinen NIE als sichtbarer Text -
 * seitenName() ist dafür die einzige Übersetzungsstelle im Frontend. */
export function SeiteLink({route, className = "", ...rest}) {
    if (!route || route === "-") {
        return <span className={`text-muted ${className}`} {...rest}>–</span>;
    }
    return (
        <Link href={`/nutzung/seiten?route=${encodeURIComponent(route)}`} className={`nutzung-link ${className}`} {...rest}>
            {seitenName(route)}
        </Link>
    );
}
